import { EditInDialogButtonProps } from '@react-admin/ra-form-layout';
import { useCallback, useMemo } from 'react';
import {
  Identifier,
  useGetIdentity,
  useNotify,
  useRecordContext,
  useRedirect,
  useRefresh,
} from 'react-admin';

import { GetB2bEmployeesOneResponse } from '@Common/types/bo/b2b/employees';
import {
  GetBoExternalEmployeeDocumentDetailsEndpoint,
  PutBoExternalEmployeesIdEndpoint,
} from '@Common/types/bo_external/employees';

import { EditInDialogButton, StepperForm, StepperFormStep } from '@sh4res/bo-common/components';
import { ContactStep } from '@sh4res/bo-common/components/B2bEmployee/steps/ContactStep';
import { EmploymentInformationStep } from '@sh4res/bo-common/components/B2bEmployee/steps/EmployeeInformationStep';
import { PersonalInformationStep } from '@sh4res/bo-common/components/B2bEmployee/steps/PersonalInformationStep';
import { createEmployeeEmailsValidator } from '@sh4res/bo-common/components/B2bEmployee/validateEmployeeEmails';
import { getErrorMessage, Resources } from '@sh4res/bo-common/utils';
import { buildLink } from '@sh4res/bo-common/utils/build_link';

import { logAction, reportError } from '@Utils/analytics';

const Form = ({ steps }: { steps: StepperFormStep[] }) => {
  const record =
    useRecordContext<GetBoExternalEmployeeDocumentDetailsEndpoint['Client']['ResponseBody']>();

  return (
    <StepperForm
      steps={steps}
      defaultValues={record}
      permissions={{
        resource: 'employees',
        action: 'edit',
      }}
      testId="BoExternalEmployeesEdit"
    />
  );
};

export const EmployeesEdit = ({
  resource,
  ...props
}: EditInDialogButtonProps['ButtonProps'] & {
  resource: Resources;
}) => {
  const notify = useNotify();
  const refresh = useRefresh();
  const redirect = useRedirect();
  const { data: identity, isLoading: isLoadingIdentity } = useGetIdentity();

  const record = useRecordContext<GetB2bEmployeesOneResponse>();

  const onError = useCallback(
    (error: unknown) => {
      reportError('Employee edit failed', error);
      notify('resources.bo_common.b2b.employees.actions.edit.failure', {
        type: 'warning',
        messageArgs: { error: getErrorMessage(error) },
        multiLine: true,
      });
    },
    [notify],
  );

  const onSuccess = useCallback(
    (response: { id: Identifier }) => {
      logAction('Employee edited', { employeeId: response.id });
      notify('resources.bo_common.b2b.employees.actions.edit.success', { type: 'success' });
      redirect(buildLink('employees', 'show', response.id));
      refresh();
    },
    [notify, redirect, refresh],
  );

  let companyId: number | undefined;
  if (identity?.companyId) {
    companyId = Array.isArray(identity.companyId) ? identity.companyId[0] : identity.companyId;
  }

  const steps = useMemo(
    (): StepperFormStep[] => [
      {
        label: 'resources.bo_common.b2b.employees.actions.edit.steps.1',
        content: (
          <PersonalInformationStep
            allowCompanyId={false}
            companyId={companyId}
            recordId={record?.id}
            ntt={record?.ntt}
          />
        ),
      },
      {
        label: 'resources.bo_common.b2b.employees.actions.edit.steps.2',
        content: (
          <EmploymentInformationStep
            disableRetirementDateEdit={record?.is_retirement_age_user_controlled}
          />
        ),
      },
      {
        label: 'resources.bo_common.b2b.employees.actions.edit.steps.3',
        content: (
          <ContactStep
            disableEmailEdit={record?.is_email_user_controlled}
            disablePhoneNumberEdit={record?.is_phone_number_user_controlled}
            disableAddressEdit={record?.is_address_user_controlled}
          />
        ),
        validate: createEmployeeEmailsValidator({
          email: record?.email,
          secondary_email: record?.secondary_email,
        }),
      },
    ],
    [
      companyId,
      record?.is_email_user_controlled,
      record?.is_phone_number_user_controlled,
      record?.is_retirement_age_user_controlled,
      record?.is_address_user_controlled,
      record?.id,
      record?.ntt,
      record?.email,
      record?.secondary_email,
    ],
  );

  if (!record) {
    return null;
  }

  if (isLoadingIdentity) {
    return null;
  }

  const handleTransform = (
    data: PutBoExternalEmployeesIdEndpoint['Client']['RequestBody'],
  ): PutBoExternalEmployeesIdEndpoint['Client']['RequestBody'] => ({
    firstname: data.firstname,
    lastname: data.lastname,
    country: record.is_address_user_controlled ? record.country : data.country,
    language: data.language,
    date_of_birth: data.date_of_birth,
    work_starting_date: data.work_starting_date,
    work_ending_date: data.work_ending_date,
    status: data.status,
    social_status: data.social_status,
    tax_number: data.tax_number,
    leaving_reason: data.leaving_reason,
    tax_residence_code: data.tax_residence_code,
    nationality: data.nationality,
    place_of_birth: data.place_of_birth,
    name_in_use: data.name_in_use,
    nir: data.nir,
    ntt: data.ntt,
    registration_number: data.registration_number,
    role_in_company: data.role_in_company,
    gender: data.gender,
    country_of_birth: data.country_of_birth,
    email: record.is_email_user_controlled ? record.email : data.email,
    phone_number: record.is_phone_number_user_controlled ? record.phone_number : data.phone_number,
    secondary_email: data.secondary_email,
    retirement_date: record.is_retirement_age_user_controlled
      ? new Date(record.retirement_date).toISOString()
      : data.retirement_date,
    street: record.is_address_user_controlled ? record.street : data.street,
    postalcode: record.is_address_user_controlled ? record.postalcode : data.postalcode,
    city: record.is_address_user_controlled ? record.city : data.city,
    csp_code: data.csp_code,
    contract_ids: data.contract_ids,
    postcode_of_birth: data.postcode_of_birth,
  });

  return (
    <EditInDialogButton
      data-testid={'BoExternalEmployeesEdit_EditButton'}
      resource={resource}
      fullWidth
      maxWidth="lg"
      title="resources.bo_common.b2b.employees.actions.edit.title"
      mutationMode="pessimistic"
      mutationOptions={{ onSuccess, onError }}
      transform={handleTransform}
      ButtonProps={{
        variant: 'contained',
        color: 'info',
        size: 'small',
        sx: {
          fontWeight: 'bold',
          textTransform: 'capitalize',
        },
        ...props,
      }}>
      <Form steps={steps} />
    </EditInDialogButton>
  );
};
